import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CodeIcon from "@material-ui/icons/Code";
import MessageVariablesPicker from "../MessageVariablesPicker";

const EMOJIS = ["😀", "😂", "😍", "😊", "🙏", "👍", "👏", "🔥", "🎉", "❤️", "😅", "🙌", "😎", "✅", "👋"];

const useStyles = makeStyles((theme) => ({
  box: {
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e7eb"}`,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  fillRoot: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  boxFill: {
    flex: 1,
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      backgroundColor: "transparent",
      "& fieldset": { border: "none" },
    },
    "& textarea": {
      fontSize: 14,
      lineHeight: 1.45,
    },
  },
  fieldFill: {
    flex: 1,
    minHeight: 0,
    height: "100%",
    "& .MuiInputBase-root": {
      height: "100%",
      alignItems: "stretch",
    },
    "& textarea": {
      height: "100% !important",
      overflowY: "auto !important",
      resize: "none",
      boxSizing: "border-box",
    },
  },
  boxGrow: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
  },
  fieldGrow: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    "& .MuiInputBase-root": {
      flex: 1,
      alignItems: "stretch",
    },
    "& textarea": {
      overflow: "hidden !important",
      resize: "none",
      boxSizing: "border-box",
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: 2,
    padding: "4px 8px",
    borderTop: `1px solid ${theme.mode === "dark" ? "#334155" : "#eef0f5"}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f8fafc",
  },
  counter: {
    marginLeft: "auto",
    fontSize: 11,
    color: theme.palette.text.secondary,
    paddingRight: 6,
  },
  emojiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 4,
    padding: 8,
  },
}));

const CampaignMessageComposer = forwardRef(({
  id,
  value = "",
  onChange,
  disabled,
  error,
  helperText,
  label,
  placeholder = "Escribe el mensaje enviado a todos los contactos...",
  maxLength = 1000,
  rows = 5,
  showCounter = true,
  allowVariables = true,
  /** "system": solo variables sin contacto (grupos y canales). */
  variablesScope = "all",
  onAttach,
  showAttach = false,
  attachDisabled = false,
  fillHeight = false,
  autoGrow = false,
  onInputScroll,
}, ref) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const lastSel = useRef({ start: (value || "").length, end: (value || "").length });
  const [emojiAnchor, setEmojiAnchor] = useState(null);

  const rememberSel = () => {
    const el = inputRef.current;
    if (!el) return;
    const len = (value || "").length;
    const start = typeof el.selectionStart === "number" ? el.selectionStart : len;
    const end = typeof el.selectionEnd === "number" ? el.selectionEnd : start;
    lastSel.current = { start, end };
  };

  const applyChange = (next) => {
    if (disabled) return;
    if (next.length > maxLength) next = next.slice(0, maxLength);
    onChange(next);
  };

  const placeCursor = (pos) => {
    lastSel.current = { start: pos, end: pos };
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try {
        el.setSelectionRange(pos, pos);
      } catch (e) {
        /* ignore */
      }
    }, 0);
  };

  const handleWrap = (before, after) => {
    rememberSel();
    const { start, end } = lastSel.current;
    const val = value || "";
    const selected = val.slice(start, end) || "texto";
    const next = `${val.slice(0, start)}${before}${selected}${after}${val.slice(end)}`;
    applyChange(next);
    placeCursor(start + before.length + selected.length + after.length);
  };

  const handleInsert = (text) => {
    rememberSel();
    const insert = String(text || "");
    if (!insert) return;
    const { start, end } = lastSel.current;
    const val = value || "";
    const next = `${val.slice(0, start)}${insert}${val.slice(end)}`;
    applyChange(next);
    placeCursor(start + insert.length);
  };

  useLayoutEffect(() => {
    if (!autoGrow) return undefined;
    const el = inputRef.current;
    if (!el) return undefined;
    el.style.minHeight = "0px";
    el.style.height = "auto";
    const content = Math.max(el.scrollHeight, rows * 22 + 28);
    el.style.minHeight = `${content}px`;
    el.style.height = "100%";
    return undefined;
  }, [value, autoGrow, rows]);

  useImperativeHandle(ref, () => ({
    insertText: handleInsert,
    focus: () => inputRef.current?.focus(),
  }));

  const handleMono = () => {
    rememberSel();
    const { start, end } = lastSel.current;
    const selected = (value || "").slice(start, end);
    if (selected.includes("\n")) handleWrap("```\n", "\n```");
    else handleWrap("```", "```");
  };

  const handleQuote = () => {
    rememberSel();
    const el = inputRef.current;
    const val = value || "";
    if (!el) {
      applyChange(val ? val.split("\n").map((line) => (line.startsWith("> ") ? line : `> ${line}`)).join("\n") : "> ");
      return;
    }
    const start = lastSel.current.start ?? 0;
    const end = lastSel.current.end ?? start;
    const lineStart = val.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = val.indexOf("\n", end);
    const sliceEnd = lineEnd === -1 ? val.length : lineEnd;
    const quoted = val
      .slice(lineStart, sliceEnd)
      .split("\n")
      .map((line) => (line.startsWith("> ") ? line : `> ${line}`))
      .join("\n");
    applyChange(`${val.slice(0, lineStart)}${quoted}${val.slice(sliceEnd)}`);
    placeCursor(lineStart + quoted.length);
  };

  return (
    <Box className={fillHeight || autoGrow ? classes.fillRoot : undefined} style={autoGrow ? { flex: 1, height: "auto", minHeight: "100%" } : undefined}>
      {label ? (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 6, fontWeight: 600 }}>
          {label}
        </Typography>
      ) : null}
      <Box
        className={`${classes.box}${fillHeight ? ` ${classes.boxFill}` : ""}${autoGrow ? ` ${classes.boxGrow}` : ""}`}
        style={error ? { borderColor: "#d32f2f" } : undefined}
      >
        <TextField
          id={id}
          inputRef={inputRef}
          className={`${classes.field}${fillHeight ? ` ${classes.fieldFill}` : ""}${autoGrow ? ` ${classes.fieldGrow}` : ""}`}
          value={value}
          onChange={(e) => applyChange(e.target.value)}
          onSelect={rememberSel}
          onKeyUp={rememberSel}
          onClick={rememberSel}
          onBlur={rememberSel}
          onScroll={onInputScroll}
          disabled={disabled}
          placeholder={placeholder}
          multiline
          rows={rows}
          fullWidth
          variant="outlined"
          inputProps={{ maxLength }}
        />
        <Box className={classes.toolbar}>
          <Tooltip title="Negrita">
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => handleWrap("*", "*")}>
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cursiva">
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => handleWrap("_", "_")}>
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Tachado">
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => handleWrap("~", "~")}>
                <FormatUnderlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Monospace (```)">
            <span>
              <IconButton size="small" disabled={disabled} onClick={handleMono}>
                <CodeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cita (> )">
            <span>
              <IconButton size="small" disabled={disabled} onClick={handleQuote}>
                <FormatQuoteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Emoji">
            <span>
              <IconButton size="small" disabled={disabled} onClick={(e) => setEmojiAnchor(e.currentTarget)}>
                <InsertEmoticonIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {showAttach ? (
            <Tooltip title="Adjuntar archivo">
              <span>
                <IconButton size="small" disabled={disabled || attachDisabled} onClick={onAttach}>
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {allowVariables ? (
            <MessageVariablesPicker
              variant="iconMenus"
              systemOnly={variablesScope === "system"}
              disabled={disabled}
              onClick={(token) => handleInsert(token)}
            />
          ) : null}
          {showCounter ? (
            <Typography className={classes.counter}>
              {value.length}/{maxLength}
            </Typography>
          ) : (
            <Box flex={1} />
          )}
        </Box>
      </Box>
      {helperText ? (
        <Typography variant="caption" color={error ? "error" : "textSecondary"}>
          {helperText}
        </Typography>
      ) : null}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
      >
        <Box className={classes.emojiGrid}>
          {EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => {
                handleInsert(emoji);
                setEmojiAnchor(null);
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>
    </Box>
  );
});

CampaignMessageComposer.displayName = "CampaignMessageComposer";

export default CampaignMessageComposer;
